'use client';

import { useCallback, useState, useRef, type ReactNode } from 'react';
import Icon from '@/components/shared/Icons';
import dynamic from 'next/dynamic';
import type { Locale } from '@/i18n';
import { formatMessage } from '@/utils/translationHelper';
import type { Criteria } from '@/fetchers/getFastResponderCriteria';
import { BUSINESS_HOURS } from '@/constants/filterConstants';

const Popper = dynamic(() => import('@/components/shared/popper/Popper'), { ssr: false });

type Props = {
  strings: {
    fast_responder_tag_tooltip: string;
    top_responder: string;
  };
  locale: Locale;
  fastResponderCriteria: Criteria;
};

function ResponderTag({ strings, locale, fastResponderCriteria }: Props): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const onDismiss = useCallback(() => {
    setIsOpen(false);
    setIsHovered(false);
  }, []);

  const onMouseEvent = useCallback(
    async e => {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(!isOpen);
      setIsHovered(!isHovered);
    },
    [isOpen, isHovered]
  );
  const onClick = useCallback(
    async e => {
      setIsOpen(!isOpen);
      setIsHovered(!isHovered);
      e.stopPropagation();
      e.preventDefault();
    },
    [isOpen, isHovered]
  );

  const flyoutText = formatMessage(strings.fast_responder_tag_tooltip, locale, {
    b: (chunks: ReactNode[]) => `<strong>${chunks}</strong>`,
    responseTime: fastResponderCriteria.thresholdResponseTime / BUSINESS_HOURS,
    responseRate: fastResponderCriteria.thresholdResponseRate
  });
  return (
    <button
      type="button"
      ref={triggerRef}
      onClick={onClick}
      onMouseEnter={onMouseEvent}
      onMouseLeave={onDismiss}
      className={isHovered ? 'text-interactive-70' : 'text-neutral-60'}
    >
      <Icon id="speed" className="float-left h-6 w-6 fill-neutral-60" />
      {isOpen && (
        <Popper triggerRef={triggerRef} isOpen={isOpen} placement="bottom-start" zIndex={1}>
          <div className="w-[309px] overflow-hidden rounded-xl bg-background shadow-4">
            <div className="m-4 flex flex-col items-start gap-1">
              <h3 className="text-b-md/tight font-bold text-[#69717A]">{strings.top_responder}</h3>
              <span
                className="leading-5 text-left text-b-md/normal font-normal text-[#1A2026]"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: flyoutText }}
              />
            </div>
          </div>
        </Popper>
      )}
    </button>
  );
}

export default ResponderTag;
